import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import {
  CheckCircle, XCircle, Clock, FileText, User, Calendar, 
  Search, MessageSquare, 
  Eye,
  Workflow, Ticket, DollarSign
} from 'lucide-react';
import { useDataContext } from '../../contexts/DataContext';
import toast from 'react-hot-toast';

const Approvals = () => {
  const { getMockData } = useDataContext();
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'history'
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');

  const loadApprovals = React.useCallback(async () => {
    try {
      setLoading(true);
      // Get pending approvals for team leads
      const data = getMockData('approverData');
      if (data) {
        setPendingApprovals(data.pendingApprovals || []);
        setApprovalHistory(data.recentActivity?.filter(a => a.type === 'approval') || []);
      }
    } catch (error) {
      console.error('Failed to load approvals:', error);
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, [getMockData]);

  useEffect(() => {
    loadApprovals();
  }, [loadApprovals]);

  const handleApprove = async (approvalId, comment = '') => {
    try {
      // In real implementation, this would call an API
      setPendingApprovals(pendingApprovals.filter(a => a.id !== approvalId));
      
      const approved = pendingApprovals.find(a => a.id === approvalId);
      if (approved) {
        setApprovalHistory([
          {
            ...approved,
            status: 'approved',
            approvedAt: new Date().toISOString(),
            comment: comment || 'Approved'
          },
          ...approvalHistory
        ]);
      }
      
      toast.success('Approval request approved successfully');
      setShowDetailsModal(false);
      setApprovalComment('');
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleReject = async (approvalId, comment = '') => {
    if (!comment.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      // In real implementation, this would call an API
      setPendingApprovals(pendingApprovals.filter(a => a.id !== approvalId));
      
      const rejected = pendingApprovals.find(a => a.id === approvalId);
      if (rejected) {
        setApprovalHistory([
          {
            ...rejected,
            status: 'rejected',
            rejectedAt: new Date().toISOString(),
            comment: comment
          },
          ...approvalHistory
        ]);
      }
      
      toast.success('Approval request rejected');
      setShowDetailsModal(false);
      setApprovalComment('');
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const handleViewDetails = (approval) => {
    setSelectedApproval(approval);
    setShowDetailsModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'approved': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'urgent': return 'bg-red-800 text-red-50 dark:bg-red-950 dark:text-red-100';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'workflow': return Workflow;
      case 'ticket': return Ticket;
      case 'change_request': return FileText;
      case 'expense': return DollarSign;
      case 'leave': return Calendar;
      default: return FileText;
    }
  };

  const filteredPending = pendingApprovals.filter(approval => {
    const matchesSearch = approval.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         approval.requester?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || approval.type === filterType;
    const matchesPriority = filterPriority === 'all' || approval.priority === filterPriority;
    return matchesSearch && matchesType && matchesPriority;
  });

  const filteredHistory = approvalHistory.filter(approval => {
    const matchesSearch = approval.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         approval.requester?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || approval.type === filterType;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading approvals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Approvals
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Review and approve workflow requests, change requests, and other items requiring your approval
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'pending'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Pending Approvals
                {pendingApprovals.length > 0 && (
                  <Badge className="bg-yellow-500 text-white">{pendingApprovals.length}</Badge>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Approval History
              </div>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search approvals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger>
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="workflow">Workflow</SelectItem>
              <SelectItem value="ticket">Ticket</SelectItem>
              <SelectItem value="change_request">Change Request</SelectItem>
              <SelectItem value="expense">Expense</SelectItem>
              <SelectItem value="leave">Leave Request</SelectItem>
            </SelectContent>
          </Select>
          {activeTab === 'pending' && (
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger>
                <SelectValue placeholder="All Priorities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Pending Approvals */}
        {activeTab === 'pending' && (
          <div className="space-y-4">
            {filteredPending.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    No Pending Approvals
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    You're all caught up! There are no items waiting for your approval.
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredPending.map((approval) => {
                const TypeIcon = getTypeIcon(approval.type);
                return (
                  <Card key={approval.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                              <TypeIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                {approval.title}
                              </h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {approval.description}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-4 mt-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                              <User className="w-4 h-4" />
                              <span>Requested by: {approval.requester}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                              <Calendar className="w-4 h-4" />
                              <span>{new Date(approval.requestedAt).toLocaleDateString()}</span>
                            </div>
                            <Badge className={getPriorityColor(approval.priority)}>
                              {approval.priority}
                            </Badge>
                            <Badge className={getStatusColor(approval.status)}>
                              {approval.status}
                            </Badge>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetails(approval)}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200 dark:bg-green-900 dark:text-green-200"
                            onClick={() => handleApprove(approval.id)}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200 dark:bg-red-900 dark:text-red-200"
                            onClick={() => {
                              setSelectedApproval(approval);
                              setShowDetailsModal(true);
                            }}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Approval History */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            {filteredHistory.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    No Approval History
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Your approval history will appear here.
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredHistory.map((approval) => {
                const TypeIcon = getTypeIcon(approval.type);
                return (
                  <Card key={approval.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                              <TypeIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                {approval.title}
                              </h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {approval.description}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-4 mt-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                              <User className="w-4 h-4" />
                              <span>Requested by: {approval.requester}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                              <Calendar className="w-4 h-4" />
                              <span>
                                {approval.status === 'approved' && approval.approvedAt
                                  ? `Approved: ${new Date(approval.approvedAt).toLocaleDateString()}`
                                  : approval.status === 'rejected' && approval.rejectedAt
                                  ? `Rejected: ${new Date(approval.rejectedAt).toLocaleDateString()}`
                                  : new Date(approval.requestedAt).toLocaleDateString()}
                              </span>
                            </div>
                            <Badge className={getStatusColor(approval.status)}>
                              {approval.status}
                            </Badge>
                            {approval.comment && (
                              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <MessageSquare className="w-4 h-4" />
                                <span>{approval.comment}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Details Modal */}
        {showDetailsModal && selectedApproval && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Approval Details</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowDetailsModal(false);
                      setSelectedApproval(null);
                      setApprovalComment('');
                    }}
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {selectedApproval.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {selectedApproval.description}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Requester
                    </label>
                    <p className="text-gray-900 dark:text-gray-100">{selectedApproval.requester}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Priority
                    </label>
                    <Badge className={getPriorityColor(selectedApproval.priority)}>
                      {selectedApproval.priority}
                    </Badge>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Type
                    </label>
                    <p className="text-gray-900 dark:text-gray-100 capitalize">
                      {selectedApproval.type?.replace('_', ' ')}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Requested Date
                    </label>
                    <p className="text-gray-900 dark:text-gray-100">
                      {new Date(selectedApproval.requestedAt).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                    Comment {selectedApproval.status === 'pending' && '(Optional for approval, required for rejection)'}
                  </label>
                  <Textarea
                    value={approvalComment}
                    onChange={(e) => setApprovalComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={3}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDetailsModal(false);
                      setSelectedApproval(null);
                      setApprovalComment('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => handleApprove(selectedApproval.id, approvalComment)}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => handleReject(selectedApproval.id, approvalComment)}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Approvals;

